import { useMemo } from "react";
import { PostgresLayerServiceConfigurationDc } from "@evergis/api";

import { getLayerInfo } from "../../evergis/core/layer/getLayerInfo";
import { getServiceConfiguration } from "../../evergis/core/layer/getServiceConfiguration";
import { getAttributesConfiguration } from "../../evergis/core/layer/getAttributesConfiguration";
import { getTableInfo } from "../../evergis/core/table/getTableInfo";
import { getLayerStyleProxy } from "../../evergis/core/layer/getLayerStyle";
import { useProjectView } from "../../ducks/project/hooks";
import { useDefaultScaleRange } from "../style/useDefaultScaleRange";
import { LayerInfo, TableInfo } from "../../interfaces/resourceInfo";
import { LayerFetchStatus, layerWithTable } from "../../resources/layerWithTable";
import { StyleProxy } from "../../evergis/core/classification/StyleProxy";

export interface PostgresLayerWithTable {
  rawConfiguration: PostgresLayerServiceConfigurationDc;
  layer: LayerInfo;
  table: TableInfo;
  segmentStyle: StyleProxy;
  status: LayerFetchStatus;
  loading: boolean;
  refetch: () => Promise<void>;
}

export const usePostgresLayerWithTable = (
  layerName: string,
  waitInput?: boolean,
  fetchQueryTable?: boolean,
): PostgresLayerWithTable => {
  const { resolution } = useProjectView();
  const defaultScaleRange = useDefaultScaleRange();
  const { data, meta, loading, refetch } = layerWithTable.use({
    input: { name: layerName, defaultScaleRange, fetchQueryTable },
    waitInput,
  });

  return useMemo(() => {
    const { layer, table } = data || {};
    const status = meta ? meta.status : void 0;

    const serviceConfiguration = getServiceConfiguration(layer);
    const attributesConfiguration = getAttributesConfiguration(layer);

    return {
      rawConfiguration: {
        ...serviceConfiguration,
        attributesConfiguration,
      },
      layer: getLayerInfo(layer, defaultScaleRange),
      segmentStyle: getLayerStyleProxy(layer, defaultScaleRange, resolution),
      table: getTableInfo(table),
      status,
      loading,
      refetch: async () => {
        await refetch();
      },
    };
  }, [data, meta, defaultScaleRange, resolution, loading, refetch]);
};

export function usePostgresLayerTableName(layerName?: string): string {
  const { table } = usePostgresLayerWithTable(layerName, true);

  return useMemo(() => table && table.name, [table]);
}
